import React from 'react';
import { Box, Typography, IconButton, InputBase, Avatar, Badge } from '@mui/material';
import {
  Search as SearchIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import Sidebar from './Sidebar';
import { colors } from '../theme/expertiseTheme';
import LogoImage from '../images/Expertise_Marca_FundosClaros_V1.png';

interface LayoutProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  showHeader?: boolean;
}

const Layout: React.FC<LayoutProps> = ({
  children,
  title,
  subtitle,
  showHeader = true,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: colors.light,
      }}
    >
      {/* Sidebar */}
      <Sidebar />

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          ml: '72px', // Sidebar width
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Top Header */}
        {showHeader && (
          <Box
            sx={{
              height: 72,
              backgroundColor: colors.white,
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 4,
              position: 'sticky',
              top: 0,
              zIndex: 1100,
            }}
          >
            {/* Left - Logo & Search */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {/* Brand */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '10px',
                    backgroundColor: colors.accent,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    component="img"
                    src={LogoImage}
                    alt="Expertise IA"
                    sx={{
                      width: 28,
                      height: 28,
                      objectFit: 'contain',
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '1.125rem',
                    color: colors.dark,
                    fontFamily: 'Outfit, sans-serif',
                  }}
                >
                  Expertise IA
                </Typography>
              </Box>

              {/* Search */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: colors.light,
                  borderRadius: '10px',
                  px: 2,
                  py: 1,
                  width: 320,
                }}
              >
                <SearchIcon sx={{ color: colors.text.light, fontSize: 20, mr: 1 }} />
                <InputBase
                  placeholder="Pesquisar..."
                  sx={{
                    flex: 1,
                    fontSize: '0.875rem',
                    color: colors.text.primary,
                    '& input::placeholder': {
                      color: colors.text.light,
                      opacity: 1,
                    },
                  }}
                />
              </Box>
            </Box>

            {/* Right - Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton
                sx={{
                  color: colors.text.secondary,
                  '&:hover': {
                    backgroundColor: colors.light,
                  },
                }}
              >
                <Badge badgeContent={3} color="error" variant="dot">
                  <NotificationsIcon fontSize="small" />
                </Badge>
              </IconButton>

              <IconButton
                sx={{
                  color: colors.text.secondary,
                  '&:hover': {
                    backgroundColor: colors.light,
                  },
                }}
              >
                <SettingsIcon fontSize="small" />
              </IconButton>

              <Avatar
                sx={{
                  width: 38,
                  height: 38,
                  backgroundColor: colors.primary,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                U
              </Avatar>
            </Box>
          </Box>
        )}

        {/* Page Title */}
        {(title || subtitle) && (
          <Box sx={{ px: 4, pt: 4, pb: 2 }}>
            {title && (
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: colors.dark,
                  fontSize: '1.75rem',
                }}
              >
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography
                variant="body2"
                sx={{
                  color: colors.text.secondary,
                  mt: 0.5,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        )}

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            px: 4,
            pb: 4,
            pt: title ? 0 : 4,
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
